import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import CircularProgress from "@material-ui/core/CircularProgress";
import Avatar from "@material-ui/core/Avatar";
import Checkbox from "@material-ui/core/Checkbox";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIcon from "@material-ui/icons/Phone";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(2, 3),
    flexShrink: 0,
    color: "#fff",
    "& .MuiTypography-root": {
      fontWeight: 600,
      fontSize: "1.2rem",
      color: "inherit",
    },
  },
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(0),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : "#fafafa",
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "60vh",
    minHeight: 400,
  },
  searchBox: {
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark" ? "#1e1e1e" : "#ffffff",
    borderBottom:
      theme.palette.type === "dark" ? "1px solid #333" : "1px solid #e0e0e0",
    flexShrink: 0,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor:
        theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
      borderRadius: 8,
      "&:hover": {
        backgroundColor:
          theme.palette.type === "dark" ? "#333" : "#eeeeee",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.type === "dark" ? "#2a2a2a" : "#ffffff",
      },
    },
  },
  selectedInfo: {
    padding: theme.spacing(1, 2),
    backgroundColor:
      theme.palette.type === "dark" ? "#1a2a1a" : "#e8f5e9",
    borderBottom:
      theme.palette.type === "dark" ? "1px solid #2a4a2a" : "1px solid #c8e6c9",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  contactList: {
    overflowY: "auto",
    flex: 1,
    padding: 0,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background:
        theme.palette.type === "dark" ? "#2a2a2a" : "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.type === "dark" ? "#555" : "#bbb",
      borderRadius: "4px",
    },
  },
  listItem: {
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(102, 126, 234, 0.05)",
    },
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },
  listItemSelected: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(102, 126, 234, 0.15)"
        : "rgba(102, 126, 234, 0.08)",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(102, 126, 234, 0.2)"
          : "rgba(102, 126, 234, 0.12)",
    },
  },
  avatar: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    fontWeight: 600,
  },
  contactName: {
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#fff" : "#333",
  },
  contactNumber: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "#aaa" : "#777",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.palette.type === "dark" ? "#aaa" : "#999",
    gap: theme.spacing(2),
  },
  loadingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : "#f5f5f5",
    borderTop:
      theme.palette.type === "dark" ? "1px solid #444" : "1px solid #e0e0e0",
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  buttonPrimary: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    boxShadow: "0 4px 15px 0 rgba(102, 126, 234, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      boxShadow: "0 6px 20px 0 rgba(102, 126, 234, 0.5)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      background: theme.palette.type === "dark" ? "#444" : "#ccc",
      color: theme.palette.type === "dark" ? "#888" : "#666",
      boxShadow: "none",
    },
    transition: "all 0.3s ease",
  },
  buttonSecondary: {
    color:
      theme.palette.type === "dark" ? "#fff" : theme.palette.primary.main,
    fontWeight: 500,
    padding: theme.spacing(1, 3),
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(102, 126, 234, 0.1)",
    },
  },
  chip: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 22,
  },
  phoneIcon: {
    fontSize: "0.9rem",
    color: theme.palette.type === "dark" ? "#aaa" : "#999",
  },
  loadMoreBtn: {
    width: "100%",
    padding: theme.spacing(1),
    color: theme.palette.type === "dark" ? "#aaa" : "#667eea",
  },
}));

const ContactListItemFromContactsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colorModeContext = useContext(ColorModeContext);
  const useGradient =
    colorModeContext?.useGradient || theme.palette?.gradient || false;

  const {
    user: { companyId },
  } = useContext(AuthContext);
  const { contactListId } = useParams();

  const [searchParam, setSearchParam] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchContacts = useCallback(async (search, page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: search, pageNumber: page },
      });
      if (page === 1) {
        setContacts(data.contacts);
      } else {
        setContacts((prev) => [...prev, ...data.contacts]);
      }
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedContacts([]);
    setPageNumber(1);
    setSearchParam("");
    fetchContacts("", 1);
  }, [open, fetchContacts]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setPageNumber(1);
      fetchContacts(searchParam, 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParam, open, fetchContacts]);

  const handleToggle = (contact) => {
    setSelectedContacts((prev) => {
      const isAlreadySelected = prev.some((c) => c.id === contact.id);
      if (isAlreadySelected) {
        return prev.filter((c) => c.id !== contact.id);
      }
      return [...prev, contact];
    });
  };

  const isSelected = (contact) =>
    selectedContacts.some((c) => c.id === contact.id);

  const handleLoadMore = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchContacts(searchParam, nextPage);
  };

  const handleSave = async () => {
    if (selectedContacts.length === 0) return;
    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    for (const contact of selectedContacts) {
      try {
        await api.post("/contact-list-items", {
          name: contact.name,
          number: contact.number,
          email: contact.email || "",
          companyId,
          contactListId,
          isWhatsappValid: true,
        });
        successCount++;
      } catch (err) {
        errorCount++;
      }
    }

    setSaving(false);

    if (successCount > 0) {
      toast.success(
        `${successCount} contato${successCount > 1 ? "s" : ""} adicionado${
          successCount > 1 ? "s" : ""
        } com sucesso!`
      );
    }
    if (errorCount > 0) {
      toast.warning(
        `${errorCount} contato${errorCount > 1 ? "s" : ""} não pud${
          errorCount > 1 ? "eram" : "e"
        } ser adicionado${errorCount > 1 ? "s" : ""}.`
      );
    }

    onClose();
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor:
            theme.palette.type === "dark" ? "#1e1e1e" : "#ffffff",
          borderRadius: isMobile ? 0 : 12,
          display: "flex",
          flexDirection: "column",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        id="contacts-from-app-dialog-title"
        className={`${classes.dialogTitle} ${
          useGradient ? classes.gradientBackground : classes.solidBackground
        }`}
      >
        {i18n.t("contactListItemFromContactsModal.title")}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Search bar */}
        <div className={classes.searchBox}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={i18n.t("contactListItemFromContactsModal.searchPlaceholder")}
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            className={classes.searchField}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {/* Selection count indicator */}
        {selectedContacts.length > 0 && (
          <div className={classes.selectedInfo}>
            <Chip
              size="small"
              className={classes.chip}
              label={selectedContacts.length}
            />
            <Typography variant="body2" style={{ color: theme.palette.type === "dark" ? "#ccc" : "#555" }}>
              {selectedContacts.length === 1
                ? i18n.t("contactListItemFromContactsModal.selectedOne")
                : i18n.t("contactListItemFromContactsModal.selectedMany")}
            </Typography>
          </div>
        )}

        {/* Contact list */}
        <div className={classes.contactList}>
          {loading && pageNumber === 1 ? (
            <div className={classes.loadingState}>
              <CircularProgress size={32} style={{ color: "#667eea" }} />
            </div>
          ) : contacts.length === 0 ? (
            <div className={classes.emptyState}>
              <PersonIcon style={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body2">
                {searchParam
                  ? i18n.t("contactListItemFromContactsModal.noContactsFound")
                  : i18n.t("contactListItemFromContactsModal.noContactsAvailable")}
              </Typography>
            </div>
          ) : (
            <List dense disablePadding>
              {contacts.map((contact, index) => (
                <React.Fragment key={contact.id}>
                  <ListItem
                    className={`${classes.listItem} ${
                      isSelected(contact) ? classes.listItemSelected : ""
                    }`}
                    onClick={() => handleToggle(contact)}
                    button
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={contact.profilePicUrl || contact.urlPicture}
                        className={classes.avatar}
                      >
                        {!contact.profilePicUrl && !contact.urlPicture && getInitials(contact.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography className={classes.contactName}>
                          {contact.name}
                        </Typography>
                      }
                      secondary={
                        <span className={classes.contactNumber}>
                          <PhoneIcon className={classes.phoneIcon} />
                          {contact.number}
                        </span>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Checkbox
                        edge="end"
                        checked={isSelected(contact)}
                        onChange={() => handleToggle(contact)}
                        color="primary"
                        style={{
                          color: isSelected(contact) ? "#667eea" : undefined,
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < contacts.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
              {hasMore && (
                <ListItem>
                  <Button
                    className={classes.loadMoreBtn}
                    onClick={handleLoadMore}
                    disabled={loading}
                    fullWidth
                  >
                    {loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      i18n.t("common.buttons.loadMore")
                    )}
                  </Button>
                </ListItem>
              )}
            </List>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          className={classes.buttonSecondary}
          disabled={saving}
          variant="text"
        >
          {i18n.t("common.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          className={classes.buttonPrimary}
          disabled={saving || selectedContacts.length === 0}
          variant="contained"
        >
          {saving ? (
            <>
              <CircularProgress
                size={16}
                style={{ marginRight: 8, color: "#fff" }}
              />
              {i18n.t("contactListItemFromContactsModal.adding")}
            </>
          ) : (
            `${i18n.t("contactListItemFromContactsModal.add")}${
              selectedContacts.length > 0
                ? ` (${selectedContacts.length})`
                : ""
            }`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactListItemFromContactsModal;
